import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { departementCourant } from '../services/droits';
import { versDate } from '../services/horodatage';
import { stageAPI, documentsAPI } from '../services/api';
// Lot D.1 : un document purgé dit « Purgé le … » à la place de « Voir ».
import { MentionPurge } from './DocumentsConservation';
import PhotoEtudiant from './PhotoEtudiant';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';
import StageFicheLiaisonContenu, { LigneEtatFiche } from './StageFicheLiaisonContenu';

/**
 * SUIVI DE STAGE — l'onglet « Dossier étudiant » (lot 3.7b)
 * ==========================================================
 * Spec     : doc/modules/suivi_de_stage.md § 5 — Dossier étudiant, écarts E130 → E133
 * Maquette : #tab-dossier — à gauche la liste des étudiants (recherche, promotion, pastille),
 * à droite le dossier : fiche de liaison, convention, visite, évaluation du maître, bandeau
 * « Stage validé » ; la spec y ajoute l'historique complet.
 *
 * ── VOIE A (validée au cadrage du 2026-10-01) ──────────────────────────────
 * Un écran de CONSULTATION, d'après la maquette, qui relit EXACTEMENT les données du panneau
 * latéral (`GET /stage/dossiers/:id`) : deux lectures de la même chose finiraient par
 * diverger. Aucun geste d'écriture ici — ils vivent dans le panneau du tableau de bord.
 */

// ⚠️ `versDate` et non `new Date` : une heure sans fuseau venant de la base est de l'UTC (E78).
const dateFr = (iso) => { const d = versDate(iso); return d ? d.toLocaleDateString('fr-FR') : null; };
const momentFr = (iso) => {
    const d = versDate(iso);
    return d ? `${d.toLocaleDateString('fr-FR')} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : null;
};
const nombre = (n) => (n === null || n === undefined ? '' : String(Number(n)).replace('.', ','));
const normaliser = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
/**
 * Toutes les familles du tableau de bord : un dossier archivé se consulte même validé ou exclu.
 * ⚠️ 2026-10-13 : « alternance » (exclu « Passé en alternance », E166) manquait — ces dossiers ne s'ouvraient plus ici.
 */
const TOUTES_FAMILLES = ['actif', 'abandon', 'etranger', 'alternance', 'valide'];
const STATUT_PIECE = { valide: ['✅ Validé', 'badge-ok'], depose: ['À vérifier', 'badge-wait'], rejete: ['Rejeté', 'dos-rejet'] };

/** Une carte du dossier — maquette : un titre à pictogramme, un état à droite, un corps. */
function Carte({ titre, etat, ton = 'ok', children }) {
    return (
        <div className="dos-carte">
            <div className="dos-carte-titre">
                <span>{titre}</span>
                {etat && <span className={'badge ' + (ton === 'ok' ? 'badge-ok' : 'badge-wait')}>{etat}</span>}
            </div>
            {children && <div className="dos-carte-corps">{children}</div>}
        </div>
    );
}

const Info = ({ label, children }) => (children ? <div><span className="dos-label">{label} :</span> <strong>{children}</strong></div> : null);

// Lot L1a (E169) : `configurationId` = la vignette ouverte — seuls les dossiers de CE processus, pas de filtre Promotion.
function StageDossier({ config, user, anneeId, dossierInitial = null, configurationId = null }) {
    const departementId = departementCourant(user, config);
    const [liste, setListe] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [recherche, setRecherche] = useState('');
    const [formation, setFormation] = useState('');
    // Lot 3.5c-3 (E163) : ouvert depuis le volet de l’onglet Évaluation, l’étudiant arrive DÉJÀ choisi.
    const [choisi, setChoisi] = useState(dossierInitial);
    const [dossier, setDossier] = useState(null);
    const [lecture, setLecture] = useState(false);
    const [erreurDossier, setErreurDossier] = useState(null);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        if (!departementId) return;
        setErreur(null);
        try {
            const r = await stageAPI.getDashboard(departementId, anneeId, { visibilite: TOUTES_FAMILLES });
            setListe(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message || 'Lecture des dossiers impossible');
        }
    }, [departementId, anneeId]);
    // ⚠️ La sélection ne se vide qu'au CHANGEMENT d'année ou de département, pas au montage : sinon l'étudiant
    // choisi d'avance (« Dossier » du volet Évaluation, E163) était effacé aussitôt (vu au banc 3.5c-3).
    // On compare à la dernière année / au dernier département VUS : le mode strict de React rejoue l'effet deux fois
    // au montage, une garde « premier passage » ne suffisait pas.
    const vu = useRef(`${departementId}|${anneeId}`);
    useEffect(() => {
        recharger();
        const cle = `${departementId}|${anneeId}`;
        if (vu.current === cle) return;
        vu.current = cle;
        setChoisi(null); setDossier(null);
    }, [recharger, departementId, anneeId]);

    useEffect(() => {
        if (!choisi) return undefined;
        let annule = false;
        setLecture(true); setErreurDossier(null);
        stageAPI.getDossier(choisi, departementId)
            .then((r) => { if (!annule) setDossier(r.data.dossier || r.data); })
            .catch((e) => { if (!annule) setErreurDossier(e.response?.data?.error || 'Lecture du dossier impossible'); })
            .finally(() => { if (!annule) setLecture(false); });
        return () => { annule = true; };
    }, [choisi, departementId]);

    const lignes = useMemo(() => {
        const q = normaliser(recherche.trim());
        return (liste?.dossiers || []).filter((d) => (configurationId ? d.configuration_id === configurationId : (!formation || String(d.sous_niveau_id) === formation))
            && (!q || normaliser(`${d.nom} ${d.prenom} ${d.entreprise_nom || ''}`).includes(q) || normaliser(`${d.prenom} ${d.nom}`).includes(q)));
    }, [liste, recherche, formation, configurationId]);

    const voir = async (documentId) => {
        const fenetre = window.open('', '_blank');
        try {
            const r = await documentsAPI.lire(documentId, departementId);
            const url = URL.createObjectURL(r.data);
            if (fenetre) fenetre.location.href = url; else window.location.href = url;
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { if (fenetre) fenetre.close(); setErreurDossier("Le document n'a pas pu être ouvert."); }
    };

    const d = dossier && dossier.id === choisi ? dossier : null;
    const histo = d?.historique || [];
    const acte = (code) => histo.find((h) => h.action === code);
    const fiche = d?.fiche;
    const conv = d?.sections?.convention;
    const docs = d?.sections?.documents;
    const evalu = d?.sections?.evaluation;
    const res = evalu?.resultat;
    // Lot 3.5c-3 : la section ⑧ du dossier porte la fiche de visite courante (`fiche`) et ses versions.
    const vis = d?.sections?.visite;
    const fv = vis?.fiche;
    const valide = acte('stage_valide');
    const exclusion = d && /^exclu_/.test(d.statut) ? acte(d.statut) : null;

    return (
        <div>
            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Rechercher un étudiant…" value={recherche} aria-label="Rechercher un étudiant" onChange={(e) => setRecherche(e.target.value)} />
                </div>
                {!configurationId && (
                <select className="input-sm stg-filtre" value={formation} aria-label="Promotion" onChange={(e) => setFormation(e.target.value)}>
                    <option value="">Toutes les promotions</option>
                    {(liste?.formations || []).map((f) => <option key={f.id} value={String(f.id)}>{f.nom}</option>)}
                </select>
                )}
            </div>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="dos-grille">
                {/* ── La liste des étudiants ── */}
                <div className="card dos-liste">
                    <div className="card-header">Étudiants {liste && <span className="dos-gris">· {lignes.length}</span>}</div>
                    <div className="dos-liste-corps" role="listbox" aria-label="Étudiants">
                        {!liste && !erreur && <div className="dos-vide"><i className="fa-solid fa-circle-notch stg-tourne" /> Lecture…</div>}
                        {liste && !lignes.length && <div className="dos-vide">Aucun étudiant ne correspond.</div>}
                        {lignes.map((l) => (
                            <button type="button" key={l.id} role="option" aria-selected={choisi === l.id}
                                    className={'dos-item' + (choisi === l.id ? ' actif' : '')} onClick={() => setChoisi(l.id)}>
                                {/* Même photo et mêmes initiales que le tableau de bord (E48). */}
                                <PhotoEtudiant chemin={l.photo_url} className="stg-avatar stg-avatar-photo" alt=""
                                               secours={<div className="stg-avatar" style={{ background: teinteAvatar(l.nom, l.prenom) }}>{initiales(l.nom, l.prenom)}</div>} />
                                <span className="dos-item-texte">
                                    <span className="dos-item-nom">{l.nom} {l.prenom}</span>
                                    <span className="dos-gris">{l.formation_nom}{l.entreprise_nom ? ` — ${l.entreprise_nom}` : ''}</span>
                                </span>
                                <span className="dos-item-picto" title={l.action_suivante?.libelle}>{l.action_suivante?.picto}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Le dossier ── */}
                <div className="card dos-detail">
                    {!choisi && <div className="dos-vide dos-vide-grand"><i className="fa-solid fa-folder-open" /> Choisissez un étudiant pour ouvrir son dossier.</div>}
                    {choisi && lecture && !d && <div className="dos-vide dos-vide-grand"><i className="fa-solid fa-circle-notch stg-tourne" /> Ouverture du dossier…</div>}
                    {erreurDossier && <div className="stg-encart stg-encart-erreur" style={{ margin: 12 }}><i className="fa-solid fa-circle-exclamation" /><span>{erreurDossier}</span></div>}
                    {d && (
                        <>
                            <div className="card-header">
                                <span>Dossier — {d.etudiant.nom} {d.etudiant.prenom}
                                    <span className="dos-gris"> · {d.formation_nom}{d.semestre ? ` · ${d.semestre}` : ''}{d.date_debut ? ` · du ${dateFr(d.date_debut)} au ${dateFr(d.date_fin)}` : ''}</span></span>
                                <span className={'badge ' + (d.statut === 'valide' ? 'badge-ok' : 'badge-wait')}>{d.action_suivante?.picto} {d.action_suivante?.libelle}</span>
                            </div>
                            <div className="dos-corps">
                                {/* 📋 La fiche de liaison */}
                                <Carte titre="📋 Fiche de liaison"
                                       etat={fiche?.soumise_maitre_le ? `✅ Reçue le ${dateFr(fiche.soumise_maitre_le)}`
                                           : fiche?.soumise_etudiant_le ? `Saisie par l'étudiant le ${dateFr(fiche.soumise_etudiant_le)} — en attente du maître` : 'Pas encore reçue'}
                                       ton={fiche?.soumise_maitre_le ? 'ok' : 'attente'}>
                                    {fiche && (
                                        <div className="dos-infos">
                                            <Info label="Entreprise">{fiche.entreprise_nom}</Info>
                                            <Info label="Adresse">{[fiche.entreprise_adresse, [fiche.entreprise_cp, fiche.entreprise_ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</Info>
                                            <Info label="Maître de stage">{[{ Madame: 'Mme', Monsieur: 'M.' }[fiche.maitre_civilite], fiche.maitre_prenom, fiche.maitre_nom].filter(Boolean).join(' ')}{fiche.maitre_fonction ? ` · ${fiche.maitre_fonction}` : ''}</Info>
                                            <Info label="Email">{fiche.maitre_email}</Info>
                                            <Info label="Tél">{fiche.maitre_telephone}</Info>
                                            <Info label="Convention de l'université">{fiche.convention_universite === null ? null : fiche.convention_universite ? 'Oui' : 'Non — convention de l\'entreprise'}</Info>
                                            {fiche.missions && <div className="dos-missions"><span className="dos-label">Missions :</span> {fiche.missions}</div>}
                                        </div>
                                    )}
                                    {/* 22/10 (maquette rév. 11) : la fiche entière se déplie ici — même contenu que la colonne du volet ;
                                        l'entreprise et le maître sont déjà au-dessus. */}
                                    {fiche && (
                                        <details className="fl-depliable">
                                            <summary><i className="fa-solid fa-file-lines" /> Voir la fiche de liaison complète</summary>
                                            <div>
                                                <LigneEtatFiche fiche={fiche} />
                                                <StageFicheLiaisonContenu fiche={fiche} sansCoordonnees />
                                            </div>
                                        </details>
                                    )}
                                </Carte>

                                {/* 📄 La convention, et les pièces déposées en retour (E131) */}
                                <Carte titre="📄 Convention de stage"
                                       etat={acte('convention_envoyee') ? `✅ Envoyée le ${dateFr(acte('convention_envoyee').date)}` : conv?.document ? 'Préparée, pas encore envoyée' : 'Pas encore préparée'}
                                       ton={acte('convention_envoyee') ? 'ok' : 'attente'}>
                                    {conv?.document && (
                                        <div className="dos-fichier">
                                            <i className="fa-solid fa-file-pdf" />
                                            <span>{conv.document.nom_fichier}</span>
                                            {conv.mode && <span className="dos-gris">· {(conv.modes || []).find((m) => m.cle === conv.mode)?.libelle || conv.mode}</span>}
                                            {conv.document.purge_le ? <MentionPurge purgeLe={conv.document.purge_le} /> : <button type="button" className="btn btn-outline btn-sm" onClick={() => voir(conv.document.id)}><i className="fa-solid fa-eye" /> Voir</button>}
                                        </div>
                                    )}
                                    {(docs?.pieces || []).some((p) => p.courante) && (
                                        <>
                                            <div className="dos-sous-titre">Documents déposés par l'étudiant</div>
                                            {docs.pieces.filter((p) => p.courante).map((p) => {
                                                const [lib, cls] = STATUT_PIECE[p.courante.statut] || [p.courante.statut, 'badge-wait'];
                                                return (
                                                    <div className="dos-fichier" key={p.attendu_id}>
                                                        <i className="fa-solid fa-file-arrow-up" />
                                                        <span><b>{p.libelle}</b> — {p.courante.nom_fichier}</span>
                                                        <span className={'badge ' + cls}>{lib}</span>
                                                        {p.courante.purge_le ? <MentionPurge purgeLe={p.courante.purge_le} /> : <button type="button" className="btn btn-outline btn-sm" onClick={() => voir(p.courante.document_id)}><i className="fa-solid fa-eye" /> Voir</button>}
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </Carte>

                                {/* 🏫 La visite */}
                                {/* Lot 3.5c-3 : la fiche de visite courante, au format de l'évaluation du maître. */}
                                <Carte titre="🏫 Fiche de visite tuteur"
                                       etat={vis?.sans_objet ? `${d.formation_nom} — Pas de visite`
                                           : fv ? `✅ Visite du ${dateFr(fv.date_visite)} · ${fv.mode_libelle} — enregistrée le ${dateFr(fv.date_soumission)}`
                                           : vis?.tuteur ? `Pas encore remplie par ${vis.tuteur.prenom || ''} ${vis.tuteur.nom}`.replace('  ', ' ') : 'Aucun tuteur affecté'}
                                       ton={fv ? 'ok' : 'attente'}>
                                    {fv && (
                                        <>
                                            <div className="dos-criteres">
                                                {fv.criteres.map((c, i) => (
                                                    <div className="dos-critere" key={i}>
                                                        <span>{c.libelle}{c.niveau && <span className="dos-gris"> · {c.niveau}</span>}</span>
                                                        <strong>{nombre(c.points)}{c.points_max !== null ? ` / ${nombre(c.points_max)}` : ''}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="dos-total"><span>Total</span><strong>{nombre(fv.total)} / {nombre(fv.total_max)}</strong></div>
                                            {fv.commentaire && <div className="dos-citation">« {fv.commentaire} »</div>}
                                            <div className="dos-gris" style={{ marginTop: 6 }}>
                                                <i className="fa-solid fa-user-pen" /> Remplie par {fv.remplie_par} · modèle « {fv.modele_nom} »
                                                {vis.precedentes?.length > 0 && ` · modifiée ${vis.precedentes.length === 1 ? 'une fois' : `${vis.precedentes.length} fois`} (versions conservées)`}
                                            </div>
                                        </>
                                    )}
                                </Carte>

                                {/* ⭐ L'évaluation du maître */}
                                <Carte titre="⭐ Évaluation maître de stage"
                                       etat={res ? `✅ Reçue le ${dateFr(res.date_soumission)}` : evalu?.active === false ? 'Sans objet pour cette formation' : evalu?.envoyee_le ? `Envoyée le ${dateFr(evalu.envoyee_le)} — en attente` : 'Pas encore envoyée'}
                                       ton={res ? 'ok' : 'attente'}>
                                    {res && (
                                        <>
                                            <div className="dos-criteres">
                                                {(res.notes || []).slice().sort((a, b) => (a.ordre || 0) - (b.ordre || 0)).map((n, i) => {
                                                    const c = (res.grille?.criteres || []).find((x) => x.id === n.critere_id);
                                                    const max = c ? Math.max(0, ...(c.niveaux || []).map((x) => Number(x.points) || 0)) : null;
                                                    return (
                                                        <div className="dos-critere" key={i}>
                                                            <span>{n.libelle}{n.intitule && <span className="dos-gris"> · {n.intitule}</span>}</span>
                                                            <strong>{nombre(n.points)}{max !== null ? ` / ${nombre(max)}` : ''}</strong>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="dos-total"><span>Total</span><strong>{nombre(res.total)} / {nombre(res.total_max)}</strong></div>
                                            {res.commentaire && <div className="dos-citation">« {res.commentaire} »</div>}
                                            <div className="dos-gris" style={{ marginTop: 6 }}><i className="fa-solid fa-signature" /> Signé par {res.signature_nom}</div>
                                        </>
                                    )}
                                </Carte>

                                {/* Le bandeau de fin : validé, ou exclu */}
                                {valide && (
                                    <div className="dos-bandeau dos-bandeau-ok">
                                        <i className="fa-solid fa-check-circle" />
                                        <div><div className="dos-bandeau-titre">Stage validé</div>
                                            <div className="dos-gris">Validé {valide.acteur ? `par ${valide.acteur}` : 'automatiquement à la réception de la grille'} le {dateFr(valide.date)}</div></div>
                                    </div>
                                )}
                                {exclusion && (
                                    <div className="dos-bandeau dos-bandeau-exclu">
                                        <i className="fa-solid fa-user-slash" />
                                        <div><div className="dos-bandeau-titre">{exclusion.libelle}</div>
                                            <div className="dos-gris">Le {dateFr(exclusion.date)}{exclusion.acteur ? ` par ${exclusion.acteur}` : ''}{d.commentaire_exclusion ? ` — ${d.commentaire_exclusion}` : ''}</div></div>
                                    </div>
                                )}

                                {/* 📝 L'historique complet (spec § 5) */}
                                <Carte titre="📝 Historique" etat={`${histo.length} acte${histo.length > 1 ? 's' : ''}`} ton="attente">
                                    {!histo.length && <div className="dos-gris">Aucun acte enregistré.</div>}
                                    {histo.map((h, i) => (
                                        <div className="dos-hist" key={i}>
                                            <span className="dos-hist-date">{momentFr(h.date)}</span>
                                            <span className="dos-hist-acteur" title={h.acteur ? undefined : "Acte d'un formulaire public, ou automatique"}>{h.acteur || '—'}</span>
                                            <span>{h.libelle || h.action}{h.mode_libelle && ` — ${h.mode_libelle}`}{h.civilite && ` — ${h.civilite}`}{h.fichier && <> — <i>{h.fichier}</i></>}{h.motif && ` — ${h.motif}`}{h.commentaire && ` — « ${h.commentaire} »`}</span>
                                        </div>
                                    ))}
                                </Carte>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default StageDossier;
